import React, { useRef } from 'react';
import { IoMdCamera, IoMdTrash } from 'react-icons/io';
import { useThemeStore } from '../../../store/themeStore';
import ListItem from '../../common/ListItem';

const CustomPhotoUpload: React.FC = () => {
    const { selectedTheme, updateTheme } = useThemeStore();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFile = (file: File) => {
        if (!file.type.startsWith('image/')) {
            alert('Please choose an image file.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert('Image too large (max 5 MB).');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result as string;
            // Apply the photo to the currently-selected theme as its bgImage.
            updateTheme(selectedTheme.id, { bgImage: dataUrl });
        };
        reader.readAsDataURL(file);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
        // Reset so the same file can be picked again later
        e.target.value = '';
    };

    const hasPhoto = selectedTheme.bgImage && selectedTheme.bgImage.startsWith('data:');

    return (
        <ListItem
            icon={<IoMdCamera size={24} className="size-full" />}
            content={
                <div className="flex w-full flex-col gap-3">
                    <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                            <span className="text-lg">🖼️ Kid Photo</span>
                            <span className="text-xs opacity-60">
                                Upload a photo as the background for &ldquo;{selectedTheme.title}&rdquo;
                            </span>
                        </div>
                        {hasPhoto && (
                            <button
                                onClick={() => updateTheme(selectedTheme.id, { bgImage: undefined })}
                                title="Remove photo"
                                className="rounded p-2"
                                style={{ backgroundColor: `${selectedTheme.color.point}33` }}
                            >
                                <IoMdTrash size={18} />
                            </button>
                        )}
                    </div>

                    {hasPhoto ? (
                        <div className="relative h-32 w-full overflow-hidden rounded-2xl">
                            <img
                                src={selectedTheme.bgImage}
                                alt="Custom background"
                                className="size-full object-cover"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="absolute bottom-2 right-2 rounded-full bg-black/60 px-3 py-1 text-sm text-white"
                            >
                                Change
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="flex h-32 w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed"
                            style={{ borderColor: `${selectedTheme.color.point}66` }}
                        >
                            <IoMdCamera size={32} className="opacity-60" />
                            <span className="text-sm opacity-60">Tap to upload a photo</span>
                        </button>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleInputChange}
                        className="hidden"
                    />
                </div>
            }
        />
    );
};

export default CustomPhotoUpload;
